import React, { Suspense } from 'react';
import { lazyWithRetry } from './utils/lazyWithRetry';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './utils/toast';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { MaintenanceAlertProvider } from './contexts/MaintenanceAlertContext';
import MiniPopupNotification from './components/maintenance/MiniPopupNotification';
import FeedbackWidget from './components/feedback/FeedbackWidget';
import Login from './pages/auth/Login';
import AccessDenied from './pages/auth/AccessDenied';
import AuthGuard from './components/AuthGuard';
import RootRedirect from './components/RootRedirect';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { RouteAwareErrorBoundary } from './components/ErrorBoundary';
// import Dashboard from './pages/Dashboard'; // Not used
import DashboardPage from './pages/DashboardPageV2';
import MasterDataPage from './pages/MasterDataPage';
import CompanyPage from './pages/master-data/CompanyPage';
import CompanyViewPage from './pages/master-data/CompanyViewPage';
import CompanyFormPage from './pages/master-data/CompanyFormPage';
import LegacyDetailRedirect from './components/detail/LegacyDetailRedirect';
import QuotationLegacyRedirect from './pages/billing/QuotationLegacyRedirect';
import SitePage from './pages/master-data/SitePage';
import SiteViewPage from './pages/master-data/SiteViewPage';
import SiteFormPage from './pages/master-data/SiteFormPage';
import ProductPage from './pages/master-data/ProductPage';
import ProductViewPage from './pages/master-data/ProductViewPage';
import ProductFormPage from './pages/master-data/ProductFormPage';
import BrandPage from './pages/master-data/BrandPage';
import DeviceModelPage from './pages/master-data/DeviceModelPage';
import DevicePage from './pages/master-data/DevicePage';
import StockIndexRedirect from './pages/stock/StockIndexRedirect';
import DeviceViewPage from './pages/master-data/DeviceViewPage';
import DeviceFormPage from './pages/master-data/DeviceFormPage';
import DeviceTypePage from './pages/master-data/DeviceTypePage';
import SimPage from './pages/master-data/SimPage';
import SimViewPage from './pages/master-data/SimViewPage';
import SimFormPage from './pages/master-data/SimFormPage';
import SimPackagePage from './pages/master-data/SimPackagePage';
import AuthorizedSignerPage from './pages/master-data/AuthorizedSignerPage';
import AuthorizedSignerViewPage from './pages/master-data/AuthorizedSignerViewPage';
import AuthorizedSignerFormPage from './pages/master-data/AuthorizedSignerFormPage';
import SupplierPage from './pages/master-data/SupplierPage';
import SupplierViewPage from './pages/master-data/SupplierViewPage';
import SupplierFormPage from './pages/master-data/SupplierFormPage';
import RentalPage from './pages/rentals/RentalPage';
import RentalDashboard from './pages/rentals/RentalDashboard';
import RentalCreatePageV2 from './pages/rentals/RentalCreatePageV2';
import RentalEditPageV2 from './pages/rentals/RentalEditPageV2';
import RentalViewPage from './pages/rentals/RentalViewPage';
import ContractPage from './pages/contracts/ContractPage';
import ContractDetailPage from './pages/contracts/ContractDetailPage';
import ContractCreatePage from './pages/contracts/ContractCreatePage';
import ContractEditPage from './pages/contracts/ContractEditPage';
const ContractPrintPage = lazyWithRetry(() => import('./pages/contracts/ContractPrintPage'));
import ContractDocumentTrackingPage from './pages/contracts/ContractDocumentTrackingPage';
import ContractDocumentLogsPage from './pages/contracts/ContractDocumentLogsPage';
const MaintenancePage = lazyWithRetry(() => import('./pages/maintenance/MaintenancePage'));
const MaintenanceFormPage = lazyWithRetry(() => import('./pages/maintenance/MaintenanceFormPage'));
const MaintenanceViewPage = lazyWithRetry(() => import('./pages/maintenance/MaintenanceViewPage'));
const FeedbackListPage = lazyWithRetry(() => import('./pages/feedback/FeedbackListPage'));
const FeedbackDetailPage = lazyWithRetry(() => import('./pages/feedback/FeedbackDetailPage'));
const DocumentPage = lazyWithRetry(() => import('./pages/documents/DocumentPage'));
const DocumentViewPage = lazyWithRetry(() => import('./pages/documents/DocumentViewPage'));
const DocumentEditPage = lazyWithRetry(() => import('./pages/documents/DocumentEditPage'));
const RentalReport = lazyWithRetry(() => import('./pages/reports/RentalReport'));
const FinancialReport = lazyWithRetry(() => import('./pages/reports/FinancialReport'));
const SettingsPage = lazyWithRetry(() => import('./pages/settings/SettingsPage'));
const ReportsPage = lazyWithRetry(() => import('./pages/reports/ReportsPage'));
const ExportPage = lazyWithRetry(() => import('./pages/import/ExportPage'));
const ExcelBulkImportPage = lazyWithRetry(() => import('./pages/import/ExcelBulkImportPage'));
const ExpiringItemsReport = lazyWithRetry(() => import('./pages/reports/ExpiringItemsReport'));
const DeviceStatusReport = lazyWithRetry(() => import('./pages/reports/DeviceStatusReport'));
const DashboardReport = lazyWithRetry(() => import('./pages/reports/DashboardReport'));
const FlowAccountSettingsPage = lazyWithRetry(() => import('./pages/settings/FlowAccountSettingsPage'));
const GeneralSettingsPage = lazyWithRetry(() => import('./pages/settings/GeneralSettingsPage'));
const UserPage = lazyWithRetry(() => import('./pages/settings/UserPage'));
const RolePage = lazyWithRetry(() => import('./pages/settings/RolePage'));
const NotificationPage = lazyWithRetry(() => import('./pages/notifications/NotificationPage'));
const NotificationSettingsPage = lazyWithRetry(() => import('./pages/settings/NotificationSettingsPage'));
const CodeTemplateSettingsPage = lazyWithRetry(() => import('./components/ui/CodeTemplateSettingsPage'));
const MaintenanceTypePage = lazyWithRetry(() => import('./pages/settings/MaintenanceTypePage'));
const RepairMethodPage = lazyWithRetry(() => import('./pages/settings/RepairMethodPage'));
const TaxInvoiceSettingsPage = lazyWithRetry(() => import('./pages/settings/TaxInvoiceSettingsPage'));
const PdfSettingsPage = lazyWithRetry(() => import('./pages/settings/PdfSettingsPage'));
const OrganizationSettingsPage = lazyWithRetry(() => import('./pages/settings/OrganizationSettingsPage'));
import NotFoundPage from './pages/NotFoundPage';
import ErrorPage from './pages/ErrorPage';
import PagePlaceholder from './components/ui/PagePlaceholder';
const ContractReportsPage = lazyWithRetry(() => import('./pages/reports/ContractReportsPage'));
const DocumentUploadPage = lazyWithRetry(() => import('./pages/documents/DocumentUploadPage'));
const CancellationTrackingDashboard = lazyWithRetry(() => import('./pages/rentals/CancellationTrackingDashboard'));
// S122-EXTQUEUE (PO 2026-08-19): หน้าคิวอนุมัติคำขอต่ออายุการเช่า
const RentalExtensionApprovalsPage = lazyWithRetry(() => import('./pages/rentals/RentalExtensionApprovalsPage'));
const EquipmentReturnDashboard = lazyWithRetry(() => import('./pages/rentals/EquipmentReturnDashboard'));
const EquipmentReturnRequestPage = lazyWithRetry(() => import('./pages/rentals/EquipmentReturnRequestPage'));
const EquipmentReturnItemRedirect = lazyWithRetry(() => import('./pages/rentals/EquipmentReturnItemRedirect'));
const CancellationNoticeViewPage = lazyWithRetry(() => import('./pages/rentals/CancellationNoticeViewPage'));
// AccessoryRequestCenter ถูกถอดออกจาก routing แล้ว (ยุบเข้า /stock/issues — PO 2026-09-07)
// ไฟล์หน้ายังอยู่ในโปรเจกต์เผื่อต้องย้อนดูโค้ดเดิม แต่ไม่มี route ไหนเรียกใช้
const DeviceMovementCenter = lazyWithRetry(() => import('./pages/rentals/DeviceMovementCenter'));
// คลังอุปกรณ์ (Phase 2-3, PO 2026-09-07)
const StockIssueCenter = lazyWithRetry(() => import('./pages/stock/StockIssueCenter'));
const StockIssueViewPage = lazyWithRetry(() => import('./pages/stock/StockIssueViewPage'));
const StockIssueFormPage = lazyWithRetry(() => import('./pages/stock/StockIssueFormPage'));
const PendingReturnsPage = lazyWithRetry(() => import('./pages/stock/PendingReturnsPage'));
const StockReceivePage = lazyWithRetry(() => import('./pages/stock/StockReceivePage'));
const StockAdjustPage = lazyWithRetry(() => import('./pages/stock/StockAdjustPage'));
const StockMovementsPage = lazyWithRetry(() => import('./pages/stock/StockMovementsPage'));
const StockSummaryPage = lazyWithRetry(() => import('./pages/stock/StockSummaryPage'));
// ใบงานเตรียมเครื่อง (PO 2026-09-08)
const PreparationJobQueuePage = lazyWithRetry(
  () => import('./pages/preparation/PreparationJobQueuePage')
);
const PreparationJobViewPage = lazyWithRetry(
  () => import('./pages/preparation/PreparationJobViewPage')
);
const InvoicesPage = lazyWithRetry(() => import('./pages/billing/InvoicesPage'));
const PaymentsPage = lazyWithRetry(() => import('./pages/billing/PaymentsPage'));
const BatchInvoicePage = lazyWithRetry(() => import('./pages/billing/BatchInvoicePage'));
const TaxInvoicePage = lazyWithRetry(() => import('./pages/billing/TaxInvoicePage'));
const CreditNotePage = lazyWithRetry(() => import('./pages/billing/CreditNotePage'));
const QuotationsPage = lazyWithRetry(() => import('./pages/billing/QuotationsPage'));
const QuotationViewPage = lazyWithRetry(() => import('./pages/billing/QuotationViewPage'));
const QuotationFormPage = lazyWithRetry(() => import('./pages/billing/QuotationFormPage'));
const InvoicePrintPage = lazyWithRetry(() => import('./pages/billing/InvoicePrintPage'));
const PaymentPrintPage = lazyWithRetry(() => import('./pages/billing/PaymentPrintPage'));
const SupplierRepairDashboard = lazyWithRetry(() => import('./pages/supplier-repairs/SupplierRepairDashboard'));
const SupplierRepairViewPage = lazyWithRetry(() => import('./pages/supplier-repairs/SupplierRepairViewPage'));
const SupplierRepairFormPage = lazyWithRetry(() => import('./pages/supplier-repairs/SupplierRepairFormPage'));
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import ProfilePage from './pages/ProfilePage';
const HelpPage = lazyWithRetry(() => import('./pages/HelpPage'));

// Placeholder for Unauthorized
const UnauthorizedPage: React.FC = () => (
  <div className="p-8 flex flex-col items-center justify-center min-h-screen">
    <h1 className="text-3xl font-bold text-danger-600 mb-4">ไม่มีสิทธิ์เข้าถึง</h1>
    <p className="text-secondary-600">คุณไม่มีสิทธิ์เข้าถึงหน้านี้</p>
  </div>
);

// Note: PagePlaceholder is imported from components/ui/PagePlaceholder

const App: React.FC = () => {
  return (
    <Router>
      <ToastProvider>
        <AuthProvider>
          <WebSocketProvider>
            <NotificationProvider>
              <MaintenanceAlertProvider>
              <RouteAwareErrorBoundary>
                {/* Global Real-time Maintenance Alert Popup - uses settings.popupDuration */}
                <MiniPopupNotification position="top-right" />
                {/* Floating bug/feedback button on every authenticated page → email via Resend */}
                <FeedbackWidget />
                <Suspense fallback={<div className="p-8 text-center text-secondary-500">กำลังโหลด...</div>}>
                <Routes>
            {/* Public routes */}
            <Route element={<AuthGuard requireAuth={false} />}>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
          </Route>
          
          {/* Protected routes */}
          <Route element={<AuthGuard requireAuth={true} />}>
            {/* Profile - ทุกคนที่ login แล้วเข้าถึงได้ */}
            <Route path="/profile" element={<ProfilePage />} />

            {/* Help Center - คู่มือการใช้งาน ทุกคนที่ login แล้วเข้าถึงได้ */}
            <Route path="/help" element={<HelpPage />} />

            {/* Dashboard - ต้องมี permission dashboard.view */}
            <Route element={<ProtectedRoute requiredPermission="dashboard.view" />}>
              <Route path="/dashboard" element={<DashboardPage />} />
            </Route>

            {/* Master Data Routes - ต้องมีอย่างน้อย 1 permission ใน master data */}
            <Route element={<ProtectedRoute requiredPermissions={[
              'companies.view',
              'sites.view',
              'products.view',
              'brands.view',
              'device_models.view',
              'device_types.view',
              'sims.view',
              'devices.view',
              'authorized_signers.view',
              'suppliers.view'
            ]} requireAll={false} />}>
              <Route path="/master-data" element={<MasterDataPage />} />
            </Route>
          </Route>

          {/* Master Data Routes with Permission Protection */}
          {/* บริษัท: ดู/เพิ่ม/แก้ไขเป็นหน้าเต็ม (มติ PO 28 ต.ค. 2026 · เดิมเป็นโมดัลในหน้ารายการ)
              guard = สิทธิ์เดียวกับปุ่มเดิมในหน้ารายการ · ลิงก์เก่า ?view=/?edit= เด้งไปหน้าเต็ม (LegacyDetailRedirect) */}
          <Route element={<ProtectedRoute requiredPermission="companies.view" />}>
            <Route
              path="/master-data/companies"
              element={
                <LegacyDetailRedirect
                  view={(id) => `/master-data/companies/${id}`}
                  edit={(id) => `/master-data/companies/${id}/edit`}
                >
                  <CompanyPage />
                </LegacyDetailRedirect>
              }
            />
            <Route path="/master-data/companies/:id" element={<CompanyViewPage />} />
          </Route>
          <Route element={<ProtectedRoute requiredPermission="companies.create" />}>
            <Route path="/master-data/companies/new" element={<CompanyFormPage />} />
          </Route>
          {/* แก้ไขต้องโหลดบริษัทก่อน (GET /companies/{id} = companies.view) → ต้องมีทั้งสองสิทธิ์ */}
          <Route element={<ProtectedRoute requiredPermissions={['companies.view', 'companies.edit']} requireAll={true} />}>
            <Route path="/master-data/companies/:id/edit" element={<CompanyFormPage />} />
          </Route>

          {/* โครงการ: ดู/เพิ่ม/แก้ไขเป็นหน้าเต็ม (มติ PO 28 ต.ค. 2026 · เดิมโมดัลในหน้ารายการ)
              guard = สิทธิ์เดียวกับปุ่มเดิมในหน้ารายการ · ลิงก์เก่า ?view= (แจ้งเตือน site_idle เก่า / bookmark) /
              ?edit= เด้งไปหน้าเต็ม (LegacyDetailRedirect) · ตัวกรอง ?company= ที่เหลือถูกเก็บเป็นปุ่มกลับ */}
          <Route element={<ProtectedRoute requiredPermission="sites.view" />}>
            <Route
              path="/master-data/sites"
              element={
                <LegacyDetailRedirect
                  view={(id) => `/master-data/sites/${id}`}
                  edit={(id) => `/master-data/sites/${id}/edit`}
                >
                  <SitePage />
                </LegacyDetailRedirect>
              }
            />
            <Route path="/master-data/sites/:id" element={<SiteViewPage />} />
          </Route>
          <Route element={<ProtectedRoute requiredPermission="sites.create" />}>
            <Route path="/master-data/sites/new" element={<SiteFormPage />} />
          </Route>
          {/* แก้ไขต้องโหลดโครงการก่อน (GET /sites/{id} = sites.view) → ต้องมีทั้งสองสิทธิ์ */}
          <Route element={<ProtectedRoute requiredPermissions={['sites.view', 'sites.edit']} requireAll={true} />}>
            <Route path="/master-data/sites/:id/edit" element={<SiteFormPage />} />
          </Route>

          {/* สินค้า: ดู/เพิ่ม/แก้ไขเป็นหน้าเต็ม (มติ PO 28 ต.ค. 2026 · เดิมเป็นโมดัลในหน้ารายการ)
              guard = สิทธิ์เดียวกับปุ่มเดิมในหน้ารายการ · ลิงก์เก่า ?view=/?edit= เด้งไปหน้าเต็ม */}
          <Route element={<ProtectedRoute requiredPermission="products.view" />}>
            <Route
              path="/master-data/products"
              element={
                <LegacyDetailRedirect
                  view={(id) => `/master-data/products/${id}`}
                  edit={(id) => `/master-data/products/${id}/edit`}
                >
                  <ProductPage />
                </LegacyDetailRedirect>
              }
            />
            <Route path="/master-data/products/:id" element={<ProductViewPage />} />
          </Route>
          <Route element={<ProtectedRoute requiredPermission="products.create" />}>
            <Route path="/master-data/products/new" element={<ProductFormPage />} />
          </Route>
          {/* แก้ไขต้องโหลดสินค้าก่อน (GET /products/{id} = products.view) → ต้องมีทั้งสองสิทธิ์ */}
          <Route element={<ProtectedRoute requiredPermissions={['products.view', 'products.edit']} requireAll={true} />}>
            <Route path="/master-data/products/:id/edit" element={<ProductFormPage />} />
          </Route>

          <Route element={<ProtectedRoute requiredPermission="brands.view" />}>
            <Route path="/master-data/brands" element={<BrandPage />} />
          </Route>

          <Route element={<ProtectedRoute requiredPermission="device_models.view" />}>
            <Route path="/master-data/device-models" element={<DeviceModelPage />} />
          </Route>

          <Route element={<ProtectedRoute requiredPermission="device_types.view" />}>
            <Route path="/master-data/device-types" element={<DeviceTypePage />} />
          </Route>

          {/* ซิม: ดู/เพิ่ม/แก้ไขเป็นหน้าเต็ม (มติ PO 28 ต.ค. 2026) · guard = สิทธิ์เดียวกับปุ่มเดิมในหน้ารายการ
              ลิงก์เก่า ?view=/?edit= เด้งไปหน้าเต็ม (LegacyDetailRedirect · utils/deviceLinks.ts) */}
          <Route element={<ProtectedRoute requiredPermission="sims.view" />}>
            <Route
              path="/master-data/sims"
              element={
                <LegacyDetailRedirect view={(id) => `/master-data/sims/${id}`} edit={(id) => `/master-data/sims/${id}/edit`}>
                  <SimPage />
                </LegacyDetailRedirect>
              }
            />
            <Route path="/master-data/sims/:id" element={<SimViewPage />} />
          </Route>
          <Route element={<ProtectedRoute requiredPermission="sims.create" />}>
            <Route path="/master-data/sims/new" element={<SimFormPage />} />
          </Route>
          {/* แก้ไขต้องโหลดซิมก่อน (GET /sims/{id} = sims.view) → ต้องมีทั้งสองสิทธิ์ */}
          <Route element={<ProtectedRoute requiredPermissions={['sims.view', 'sims.edit']} requireAll={true} />}>
            <Route path="/master-data/sims/:id/edit" element={<SimFormPage />} />
          </Route>

          {/* Sim Packages - ใช้ sims.view permission เดียวกับ sims */}
          <Route element={<ProtectedRoute requiredPermission="sims.view" />}>
            <Route path="/master-data/sim-packages" element={<SimPackagePage />} />
          </Route>

          {/* อุปกรณ์: ดู/เพิ่ม/แก้ไขเป็นหน้าเต็ม (มติ PO 28 ต.ค. 2026) · ลิงก์เก่า ?view=<id> มีหลายสิบที่
              (การแจ้งเตือน device/device_low_stock · bookmark · แชท) → LegacyDetailRedirect เด้งไปหน้าเต็ม */}
          <Route element={<ProtectedRoute requiredPermission="devices.view" />}>
            <Route
              path="/master-data/devices"
              element={
                <LegacyDetailRedirect view={(id) => `/master-data/devices/${id}`} edit={(id) => `/master-data/devices/${id}/edit`}>
                  <DevicePage />
                </LegacyDetailRedirect>
              }
            />
            <Route path="/master-data/devices/:id" element={<DeviceViewPage />} />
          </Route>
          <Route element={<ProtectedRoute requiredPermission="devices.create" />}>
            <Route path="/master-data/devices/new" element={<DeviceFormPage />} />
          </Route>
          {/* แก้ไขต้องโหลดเครื่องก่อน (GET /devices/{id} = devices.view) → ต้องมีทั้งสองสิทธิ์ */}
          <Route element={<ProtectedRoute requiredPermissions={['devices.view', 'devices.edit']} requireAll={true} />}>
            <Route path="/master-data/devices/:id/edit" element={<DeviceFormPage />} />
          </Route>

          {/* ผู้มีอำนาจลงนาม: ดู/เพิ่ม/แก้ไขเป็นหน้าเต็ม (มติ PO 28 ต.ค. 2026 · เดิมเป็นโมดัลในหน้ารายการ)
              guard = สิทธิ์เดียวกับปุ่มเดิม · ลิงก์เก่า ?view=/?edit= เด้งไปหน้าเต็ม (LegacyDetailRedirect) */}
          <Route element={<ProtectedRoute requiredPermission="authorized_signers.view" />}>
            <Route
              path="/master-data/authorized-signers"
              element={
                <LegacyDetailRedirect
                  view={(id) => `/master-data/authorized-signers/${id}`}
                  edit={(id) => `/master-data/authorized-signers/${id}/edit`}
                >
                  <AuthorizedSignerPage />
                </LegacyDetailRedirect>
              }
            />
            <Route path="/master-data/authorized-signers/:id" element={<AuthorizedSignerViewPage />} />
          </Route>
          <Route element={<ProtectedRoute requiredPermission="authorized_signers.create" />}>
            <Route path="/master-data/authorized-signers/new" element={<AuthorizedSignerFormPage />} />
          </Route>
          {/* แก้ไขต้องโหลดรายการก่อน (GET /authorized-signers/{id} = authorized_signers.view) → ต้องมีทั้งสองสิทธิ์ */}
          <Route element={<ProtectedRoute requiredPermissions={['authorized_signers.view', 'authorized_signers.edit']} requireAll={true} />}>
            <Route path="/master-data/authorized-signers/:id/edit" element={<AuthorizedSignerFormPage />} />
          </Route>

          {/* ผู้จำหน่าย: ดู/เพิ่ม/แก้ไขเป็นหน้าเต็ม (มติ PO 28 ต.ค. 2026 · เดิมโมดัล) · ลิงก์เก่า ?view=/?edit= เด้งไปหน้าเต็ม */}
          <Route element={<ProtectedRoute requiredPermission="suppliers.view" />}>
            <Route
              path="/master-data/suppliers"
              element={
                <LegacyDetailRedirect
                  view={(id) => `/master-data/suppliers/${id}`}
                  edit={(id) => `/master-data/suppliers/${id}/edit`}
                >
                  <SupplierPage />
                </LegacyDetailRedirect>
              }
            />
            <Route path="/master-data/suppliers/:id" element={<SupplierViewPage />} />
          </Route>
          <Route element={<ProtectedRoute requiredPermission="suppliers.create" />}>
            <Route path="/master-data/suppliers/new" element={<SupplierFormPage />} />
          </Route>
          {/* แก้ไขต้องโหลดผู้จำหน่ายก่อน (GET /suppliers/{id} = suppliers.view) → ต้องมีทั้งสองสิทธิ์ */}
          <Route element={<ProtectedRoute requiredPermissions={['suppliers.view', 'suppliers.edit']} requireAll={true} />}>
            <Route path="/master-data/suppliers/:id/edit" element={<SupplierFormPage />} />
          </Route>

          {/* Rental Routes with Permission Protection */}
          <Route element={<ProtectedRoute requiredPermission="rentals.view" />}>
            <Route path="/rentals" element={<RentalDashboard />} />
            <Route path="/rentals/list" element={<RentalPage />} />
            <Route path="/rentals/edit/:id" element={<RentalEditPageV2 />} />
            <Route path="/rentals/view/:id" element={<RentalViewPage />} />
            <Route path="/rentals/cancellation-tracking" element={<CancellationTrackingDashboard />} />
            {/* หน้าใบแจ้งยกเลิก (28 ต.ค. 2026 · แทนโมดัลดู) — GET /rentals/cancellation-notices/{id} = rentals.view */}
            <Route path="/rentals/cancellation-tracking/:id" element={<CancellationNoticeViewPage />} />
          </Route>

          {/* G-17: เมนู "สร้างรายการเช่าใหม่" โชว์ด้วย rentals.create → route ต้องรับ
              .view หรือ .create (OR) ไม่งั้น role ที่มีแค่ .create คลิกเมนูแล้วเด้ง access-denied */}
          <Route element={<ProtectedRoute requiredPermissions={['rentals.view', 'rentals.create']} requireAll={false} />}>
            <Route path="/rentals/create" element={<RentalCreatePageV2 />} />
          </Route>

          {/* S122-EXTQUEUE: คิวอนุมัติต่ออายุ — gate ตรงกับ GET /rentals/extensions/pending */}
          <Route element={<ProtectedRoute requiredPermission="rental_extensions.view" />}>
            <Route path="/rentals/extension-approvals" element={<RentalExtensionApprovalsPage />} />
          </Route>

          {/* Equipment Returns - ใช้ equipment_returns.view */}
          <Route element={<ProtectedRoute requiredPermission="equipment_returns.view" />}>
            <Route path="/rentals/equipment-returns" element={<EquipmentReturnDashboard />} />
            {/* หน้าใบตามเก็บ (28 ต.ค. 2026 · แทนโมดัลรายละเอียด) — GET /equipment-returns/requests/{id} = equipment_returns.view
                ลิงก์ราย "ชิ้น" /items/:itemId เด้งไปใบของมัน (?item=) · ประกาศก่อน :id ไม่จำเป็น (RR6 จัดอันดับ static ก่อน) */}
            <Route path="/rentals/equipment-returns/items/:itemId" element={<EquipmentReturnItemRedirect />} />
            <Route path="/rentals/equipment-returns/:id" element={<EquipmentReturnRequestPage />} />
          </Route>

          {/* Device Replacement — "ศูนย์เปลี่ยนอุปกรณ์" ถูกยุบเข้าโมดูลซ่อมบำรุงเป็นแท็บ (PO 2026-09-07)
              คง route เดิมไว้เป็น redirect เพราะคู่มือ Support / cheat-sheet ยังอ้าง URL นี้ */}
          <Route element={<ProtectedRoute requiredPermission="device_replacements.view" />}>
            <Route
              path="/rentals/device-replacement"
              element={<Navigate to="/maintenance/list?tab=replacements" replace />}
            />
          </Route>

          {/* Accessory Requests — "ศูนย์ขออุปกรณ์เสริม" ถูกยุบเข้า "ใบเบิก-ยืมอุปกรณ์" (PO 2026-09-07)
              คง route เดิมไว้เป็น redirect เพราะคู่มือ Support / bookmark ยังอ้าง URL นี้ ·
              guard เป็น OR ของสิทธิ์เดิมและสิทธิ์ใหม่ เพื่อไม่ให้ role ที่ได้ stock_issues.view
              แล้วแต่ยังไม่ได้ถอน accessory_requests.view (หรือกลับกัน) เจอ access-denied */}
          <Route
            element={
              <ProtectedRoute
                requiredPermissions={['stock_issues.view', 'accessory_requests.view']}
                requireAll={false}
              />
            }
          >
            <Route
              path="/rentals/accessory-requests"
              element={<Navigate to="/stock/issues" replace />}
            />
          </Route>

          {/* คลังอุปกรณ์ (Phase 2-3) */}
          <Route element={<ProtectedRoute requiredPermission="stock_issues.view" />}>
            <Route path="/stock/issues" element={<StockIssueCenter />} />
            {/* ใบเบิก-ยืม หน้าเต็ม (มติ PO 28 ต.ค. 2026 — แทนโมดัลรายละเอียด) · รับคืน/ตัดจำหน่าย/ยกเลิก ยังเป็นโมดัลในหน้านี้ */}
            <Route path="/stock/issues/:id" element={<StockIssueViewPage />} />
            <Route path="/stock/pending-returns" element={<PendingReturnsPage />} />
          </Route>
          {/* สร้างใบเบิก = สิทธิ์เดียวกับปุ่ม "สร้างใบเบิก"/"เบิกอุปกรณ์" เดิม · รองรับ ?rental_id= (การ์ดในหน้าสัญญาเช่า) */}
          <Route element={<ProtectedRoute requiredPermission="stock_issues.create" />}>
            <Route path="/stock/issues/new" element={<StockIssueFormPage />} />
          </Route>

          <Route element={<ProtectedRoute requiredPermission="stock_movements.adjust" />}>
            <Route path="/stock/receive" element={<StockReceivePage />} />
            <Route path="/stock/adjust" element={<StockAdjustPage />} />
          </Route>

          <Route element={<ProtectedRoute requiredPermission="stock_movements.view" />}>
            <Route path="/stock/movements" element={<StockMovementsPage />} />
            {/* FB-2026-0020: สรุปคงเหลือต่อรุ่น — สิทธิ์เดียวกับ backend GET /stock/summary */}
            <Route path="/stock/summary" element={<StockSummaryPage />} />
          </Route>

          {/* /stock → เมนูแรกของกลุ่มที่ผู้ใช้เข้าได้ (FB-2026-0020: สรุปคงเหลือ ถ้ามี stock_movements.view
              ไม่งั้นใบเบิก-ยืมตามเดิม) · ProtectedRoute ของปลายทางจัดการเรื่องสิทธิ์ต่อเอง */}
          <Route path="/stock" element={<StockIndexRedirect />} />

          {/* Device Movements (ย้ายอุปกรณ์ข้ามโครงการ) - ใช้ device_movements.view */}
          <Route element={<ProtectedRoute requiredPermission="device_movements.view" />}>
            <Route path="/rentals/device-movements" element={<DeviceMovementCenter />} />
          </Route>

          {/* ใบงานเตรียมเครื่อง (PO 2026-09-08) — เซลส่งงานให้ทีมเตรียมเครื่องก่อนส่งมอบ
              guard เป็น OR ของ .view/.create เพราะเมนูโชว์ด้วย union เดียวกัน (pattern G-17):
              role ที่ได้แค่ .create (เซล) ต้องเข้าคิวงานเพื่อกดปุ่มเปิดใบได้ */}
          <Route
            element={
              <ProtectedRoute
                requiredPermissions={['preparation_jobs.view', 'preparation_jobs.create']}
                requireAll={false}
              />
            }
          >
            <Route path="/rentals/preparation-jobs" element={<PreparationJobQueuePage />} />
          </Route>

          <Route element={<ProtectedRoute requiredPermission="preparation_jobs.view" />}>
            <Route path="/rentals/preparation-jobs/:id" element={<PreparationJobViewPage />} />
          </Route>

          {/* Feedback Tracker — รายการ gate feedback.view · รายละเอียดไม่ gate (ผู้แจ้งเข้าได้) backend ตอบ 404 เอง */}
          <Route element={<ProtectedRoute requiredPermission="feedback.view" />}>
            <Route path="/feedback" element={<FeedbackListPage />} />
          </Route>
          <Route path="/feedback/:id" element={<FeedbackDetailPage />} />

          {/* Contract Routes with Permission Protection */}
          <Route element={<ProtectedRoute requiredPermission="contracts.view" />}>
            <Route path="/contracts" element={<Navigate to="/contracts/list" replace />} />
            <Route path="/contracts/list" element={<ContractPage />} />
            <Route path="/contracts/tracking" element={<ContractDocumentTrackingPage />} />
            <Route path="/contracts/:id/logs" element={<ContractDocumentLogsPage />} />
            <Route path="/contracts/view/:id" element={<ContractDetailPage />} />
            <Route path="/contracts/edit/:id" element={<ContractEditPage />} />
            <Route path="/contracts/print/:id" element={<ContractPrintPage />} />
            <Route path="/contracts/:id" element={<ContractDetailPage />} />
          </Route>

          {/* G-17: เมนู "สร้างสัญญาเช่าใหม่" โชว์ด้วย contracts.create → guard เป็น OR ของ .view/.create */}
          <Route element={<ProtectedRoute requiredPermissions={['contracts.view', 'contracts.create']} requireAll={false} />}>
            <Route path="/contracts/create" element={<ContractCreatePage />} />
          </Route>

          {/* Report Routes with Permission Protection */}
          <Route element={<ProtectedRoute requiredPermission="reports.view" />}>
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/reports/dashboard" element={<DashboardReport />} />
            <Route path="/reports/rentals" element={<RentalReport />} />
            <Route path="/reports/financial" element={<FinancialReport />} />
            <Route path="/reports/contracts" element={<ContractReportsPage />} />
            <Route path="/reports/devices" element={<DeviceStatusReport />} />
            <Route path="/reports/expiring" element={<ExpiringItemsReport />} />
          </Route>

          {/* Maintenance Routes with Permission Protection */}
          <Route element={<ProtectedRoute requiredPermission="maintenance.view" />}>
            <Route path="/maintenance/edit/:id" element={<MaintenanceFormPage />} />
            <Route path="/maintenance/view/:id" element={<MaintenanceViewPage />} />
          </Route>

          {/* หน้ารายการซ่อมบำรุงมี 2 แท็บหลังยุบ "ศูนย์เปลี่ยนอุปกรณ์" เข้ามา (PO 2026-09-07)
              → guard เป็น OR ของ maintenance.view / device_replacements.view เพื่อไม่ให้ role ที่มีแค่
              สิทธิ์ดูการเปลี่ยนอุปกรณ์เสียความสามารถเดิม (หน้าเปิดเฉพาะแท็บที่ตัวเองมีสิทธิ์) */}
          <Route element={<ProtectedRoute requiredPermissions={['maintenance.view', 'device_replacements.view']} requireAll={false} />}>
            <Route path="/maintenance" element={<Navigate to="/maintenance/list" replace />} />
            <Route path="/maintenance/list" element={<MaintenancePage />} />
          </Route>

          {/* G-17: เมนู "บันทึกการซ่อมบำรุง" โชว์ด้วย maintenance.create → guard เป็น OR ของ .view/.create
              (รวม /maintenance/create/:device_id ซึ่งเป็นหน้า create เดียวกัน แค่ preselect อุปกรณ์) */}
          <Route element={<ProtectedRoute requiredPermissions={['maintenance.view', 'maintenance.create']} requireAll={false} />}>
            <Route path="/maintenance/create" element={<MaintenanceFormPage />} />
            <Route path="/maintenance/create/:device_id" element={<MaintenanceFormPage />} />
          </Route>

          {/* Supplier Repair Routes with Permission Protection */}
          {/* ใบส่งซ่อม: ดู/สร้าง/แก้ไขเป็นหน้าเต็ม (มติ PO 28 ต.ค. 2026 · เดิมโมดัล) · เปลี่ยนสถานะยังเป็นโมดัล
              · ลิงก์เก่า ?view=/?edit= เด้งไปหน้าเต็ม */}
          <Route element={<ProtectedRoute requiredPermission="supplier_repairs.view" />}>
            <Route
              path="/supplier-repairs"
              element={
                <LegacyDetailRedirect
                  view={(id) => `/supplier-repairs/${id}`}
                  edit={(id) => `/supplier-repairs/${id}/edit`}
                >
                  <SupplierRepairDashboard />
                </LegacyDetailRedirect>
              }
            />
            <Route path="/supplier-repairs/:id" element={<SupplierRepairViewPage />} />
          </Route>
          <Route element={<ProtectedRoute requiredPermission="supplier_repairs.create" />}>
            <Route path="/supplier-repairs/new" element={<SupplierRepairFormPage />} />
          </Route>
          {/* แก้ไขต้องโหลดใบก่อน (GET /supplier-repairs/{id} = supplier_repairs.view) → ต้องมีทั้งสองสิทธิ์ */}
          <Route element={<ProtectedRoute requiredPermissions={['supplier_repairs.view', 'supplier_repairs.edit']} requireAll={true} />}>
            <Route path="/supplier-repairs/:id/edit" element={<SupplierRepairFormPage />} />
          </Route>

          {/* Document Routes with Permission Protection */}
          <Route element={<ProtectedRoute requiredPermission="documents.view" />}>
            <Route path="/documents" element={<Navigate to="/documents/list" replace />} />
            <Route path="/documents/list" element={<DocumentPage />} />
            <Route path="/documents/view/:id" element={<DocumentViewPage />} />
            <Route path="/documents/edit/:id" element={<DocumentEditPage />} />
          </Route>

          {/* G-17: เมนู "อัพโหลดเอกสาร" โชว์ด้วย documents.create → guard เป็น OR ของ .view/.create */}
          <Route element={<ProtectedRoute requiredPermissions={['documents.view', 'documents.create']} requireAll={false} />}>
            <Route path="/documents/upload" element={<DocumentUploadPage />} />
          </Route>

          {/* Notifications - ต้องมี permission notifications.view */}
          <Route element={<ProtectedRoute requiredPermission="notifications.view" />}>
            <Route path="/notifications" element={<NotificationPage />} />
            {/* ตั้งค่าการแจ้งเตือนเป็น preference ส่วนตัว (เสียง/popup/tab flash ใน localStorage)
                จึงผูกกับ notifications.view ไม่ใช่ settings.view (ปุ่มเฟืองใน NotificationBell ลิงก์มาที่นี่) */}
            <Route path="/settings/notifications" element={<NotificationSettingsPage />} />
          </Route>

          {/* Billing Routes with Permission Protection */}
          {/* G-18: redirect ให้ตรง submenu[0] ของเมนู "การเงิน" (parentMenuTarget ใน Layout เช็ค child ตัวแรก แล้วลิงก์ไป /billing)
              FB-2026-0005 (3 พ.ย. 2026): ใบเสนอราคาย้ายไปกลุ่ม "การเช่า" → submenu[0] = ใบแจ้งหนี้ · ถ้ายังชี้ quotations
              กด "การเงิน" แล้วไปโผล่หน้าใบเสนอราคา และ role ที่มี invoices.view แต่ไม่มี quotations.view เด้ง access-denied */}
          <Route path="/billing" element={<Navigate to="/billing/invoices" replace />} />
          <Route element={<ProtectedRoute requiredPermission="invoices.view" />}>
            <Route path="/billing/invoices" element={<InvoicesPage />} />
            <Route path="/billing/invoices/:id/print" element={<InvoicePrintPage />} />
          </Route>

          <Route element={<ProtectedRoute requiredPermission="payments.view" />}>
            <Route path="/billing/payments" element={<PaymentsPage />} />
            <Route path="/billing/payments/:id/print" element={<PaymentPrintPage />} />
          </Route>

          <Route element={<ProtectedRoute requiredPermission="batch_invoices.view" />}>
            <Route path="/billing/batch-invoices" element={<BatchInvoicePage />} />
          </Route>

          <Route element={<ProtectedRoute requiredPermission="tax_invoices.view" />}>
            <Route path="/billing/tax-invoices" element={<TaxInvoicePage />} />
          </Route>

          <Route element={<ProtectedRoute requiredPermission="credit_notes.view" />}>
            <Route path="/billing/credit-notes" element={<CreditNotePage />} />
          </Route>

          <Route element={<ProtectedRoute requiredPermission="quotations.view" />}>
            {/* ลิงก์เก่า ?open= / ?view= / ?edit= (สมัยโมดัล) → หน้าเต็ม */}
            <Route path="/billing/quotations" element={<QuotationLegacyRedirect><QuotationsPage /></QuotationLegacyRedirect>} />
            {/* หน้ารายละเอียดเต็ม (มติ PO 28 ต.ค. 2026 — เดิม render หน้ารายการแล้วเปิดโมดัลทับ) · ลิงก์จากใบงาน/สัญญา/บริษัท/แจ้งเตือน */}
            <Route path="/billing/quotations/:id" element={<QuotationViewPage />} />
          </Route>
          <Route element={<ProtectedRoute requiredPermission="quotations.create" />}>
            <Route path="/billing/quotations/new" element={<QuotationFormPage />} />
          </Route>
          {/* แก้ไขต้องโหลดใบก่อน (GET = quotations.view) → ต้องมีทั้งสองสิทธิ์ · ใบร่างเท่านั้น (หน้าแจ้งเหตุผลเอง) */}
          <Route element={<ProtectedRoute requiredPermissions={['quotations.view', 'quotations.edit']} requireAll={true} />}>
            <Route path="/billing/quotations/:id/edit" element={<QuotationFormPage />} />
          </Route>

          {/* Admin routes - Users & Roles with Permission Protection */}
          <Route element={<ProtectedRoute requiredPermission="users.view" />}>
            <Route path="/users" element={<Navigate to="/users/list" replace />} />
            <Route path="/users/list" element={<UserPage />} />
          </Route>

          <Route element={<ProtectedRoute requiredPermission="roles.view" />}>
            <Route path="/users/roles" element={<RolePage />} />
          </Route>

          {/* Settings Routes with Permission Protection */}
          <Route element={<ProtectedRoute requiredPermission="settings.view" />}>
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/settings/general" element={<GeneralSettingsPage />} />
            <Route path="/settings/tax-invoice" element={<TaxInvoiceSettingsPage />} />
            <Route path="/settings/pdf-settings" element={<PdfSettingsPage />} />
            <Route path="/settings/organization" element={<OrganizationSettingsPage />} />
          </Route>

          {/* Maintenance Types - ใช้ maintenance_types.view */}
          <Route element={<ProtectedRoute requiredPermission="maintenance_types.view" />}>
            <Route path="/settings/maintenance-types" element={<MaintenanceTypePage />} />
          </Route>

          {/* Repair Methods - ใช้ repair_methods.view */}
          <Route element={<ProtectedRoute requiredPermission="repair_methods.view" />}>
            <Route path="/settings/repair-methods" element={<RepairMethodPage />} />
          </Route>

          {/* Flow Account Settings — G-25: backend ใช้ sync.view/sync.create เท่านั้น
              (sync.flowaccount ไม่มีจริงฝั่ง backend) จึง gate หน้าด้วย sync.view */}
          <Route element={<ProtectedRoute requiredPermission="sync.view" />}>
            <Route path="/settings/flow-account" element={<FlowAccountSettingsPage />} />
          </Route>

          {/* Code Templates Settings - ต้องมี permission settings.edit */}
          <Route element={<ProtectedRoute requiredPermission="settings.edit" />}>
            <Route path="/settings/code-templates" element={<CodeTemplateSettingsPage />} />
          </Route>

          {/* Import/Export Routes
              excel-bulk gate ด้วย imports.view (หมวด "การนำเข้า" แยกใน Role settings —
              เดิม G-08 ใช้ OR ของ 10 key create/edit) · export gate ด้วย exports.view
              (/import redirect ไป excel-bulk — ถ้าไม่มีสิทธิ์ ProtectedRoute จัดการเด้งตามปกติ) */}
          <Route path="/import" element={<Navigate to="/import/excel-bulk" replace />} />
          <Route element={<ProtectedRoute requiredPermission="imports.view" />}>
            <Route path="/import/excel-bulk" element={<ExcelBulkImportPage />} />
          </Route>
          <Route element={<ProtectedRoute requiredPermission="exports.view" />}>
            <Route path="/import/export" element={<ExportPage />} />
          </Route>

          {/* Static pages */}
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          <Route path="/access-denied" element={<AccessDenied />} />
          
          {/* Error pages */}
          <Route path="/error" element={<ErrorPage />} />
          <Route path="/404" element={<NotFoundPage />} />

          {/* Redirects */}
          <Route path="/" element={<RootRedirect />} />
          <Route path="*" element={<NotFoundPage />} />
                </Routes>
                </Suspense>
              </RouteAwareErrorBoundary>
              </MaintenanceAlertProvider>
            </NotificationProvider>
          </WebSocketProvider>
        </AuthProvider>
      </ToastProvider>
    </Router>
  );
};

export default App;